import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  createLicensePlan,
  deleteLicensePlan,
  listLicensePlans,
  updateLicensePlan,
  type CreateLicensePlanRequest,
  type LicensePlanDefinitionResponse,
  type UpdateLicensePlanRequest,
} from '../api';

export function useLicensePlans(): UseQueryResult<LicensePlanDefinitionResponse[]> {
  return useQuery({
    queryKey: AdminQueryKeys.plans(),
    queryFn: ({ signal }) => listLicensePlans(signal),
    staleTime: 60_000,
  });
}

export function useUpdateLicensePlan() {
  const qc = useQueryClient();
  return useMutation<LicensePlanDefinitionResponse, Error, { licenseType: string; request: UpdateLicensePlanRequest }>({
    mutationFn: ({ licenseType, request }) => updateLicensePlan(licenseType, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.plans() });
    },
  });
}

export function useCreateLicensePlan() {
  const qc = useQueryClient();
  return useMutation<LicensePlanDefinitionResponse, Error, CreateLicensePlanRequest>({
    mutationFn: (request) => createLicensePlan(request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.plans() });
    },
  });
}

export function useDeleteLicensePlan() {
  const qc = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: (licenseType) => deleteLicensePlan(licenseType),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.plans() });
    },
  });
}
